import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Modal } from '@mui/material';
import { ModalInner } from '@/components/Board/BoardDetailModal/styles';
import useLayoutSize from '@/hooks/useLayoutSize';
import { LayoutType } from '@/constant/layout';
import TalkFLow from '@/components/Record/TalkFLow';
import { filterToParams } from '@/components/Record/util/function';
import { INIT_RECORD_STATE } from '@/recoil/init_data';
import { FormProvider, useForm } from 'react-hook-form';
import { RecordFilter } from '@/types/api';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import LayoutLoading from '@/components/Record/LayoutLoading';
import { useGTMEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { EventPageAreaType, GTMEventName } from '@/constant/gtm';
import { getDocumentDetailParam } from '@/utils/gtm/work';
import useDocumentData from '@/components/Document/@hooks/useDocumentData';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { useTranslation } from 'react-i18next';

export type TalkFlowModalRef = {
  open: (docId: string) => void;
  close: () => void;
};

export type TalkFlowModalProps = {
  onAfterClose?: () => void;
  pageArea?: EventPageAreaType;
};

const TalkFLowModal = forwardRef<TalkFlowModalRef, TalkFlowModalProps>(
  ({ onAfterClose, pageArea }, ref) => {
    const eventTracker = pageArea ? useGTMEventTracker(pageArea) : null;

    const dialog = useDialog();
    const toast = useSnackbar();
    const { t } = useTranslation();

    const { passportId } = useAtomValue(MyInfoState);
    const methods = useForm<RecordFilter>({ defaultValues: INIT_RECORD_STATE });
    const isOwner = methods.getValues('createdBy')?.passportId === passportId;

    const { isMobile, maxDesktopWidth } = useLayoutSize(LayoutType.WORK_MODAL);

    const [currentDocumentId, setCurrentDocumentId] = useState('');
    const isOpen = Boolean(currentDocumentId);

    const [uploading, setUploading] = useState(false);

    const modalInnerRef = useRef<HTMLDivElement>(null);

    const { error, isLoading, editDocument, initialize } = useDocumentData(
      currentDocumentId,
      methods,
    );

    const resetModal = useCallback(() => {
      onAfterClose?.();
      setCurrentDocumentId('');
      initialize();
    }, [onAfterClose, initialize]);

    const close = () =>
      methods.handleSubmit(async (data: RecordFilter) => {
        try {
          setUploading(true);
          if (!isOwner || isLoading) {
            return;
          }

          await editDocument(
            filterToParams({
              ...data,
              mode: data.mode,
            }),
          );
        } catch (e) {
          console.error(e);
          toast.showError({
            message: t('저장에 실패했습니다. 잠시 후 다시 시도해주세요.'),
          });
        } finally {
          setUploading(false);
          resetModal();
        }
      })();

    const closeModal = async () => {
      try {
        resetModal();
      } catch (e) {
        console.log(e);
      }
    };

    const sendGTMEvent = (type: '확장해서 보기' | '축소해서 보기' | '닫기') => {
      if (!eventTracker) return;

      // GA Work - Document 12번
      eventTracker(GTMEventName.clickPostAction, {
        area_name: '문서',
        action_type: type,
        ...getDocumentDetailParam(methods.getValues()),
        state: undefined,
      });
    };

    useEffect(() => {
      if (error) {
        dialog.show({
          message: error.response?.data?.message || '에러가 발생했습니다.',
          onConfirm: resetModal,
        });
      }
    }, [error, resetModal]);

    // Ref 함수 정의
    useImperativeHandle(ref, () => ({
      open: setCurrentDocumentId,
      close,
    }));

    const [showScrollToTopButton, setShowScrollToTopButton] = useState(true);

    return (
      <Modal
        open={isOpen}
        onClose={close}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        sx={{
          '> .MuiBox-root:focus': { outline: 'none' },
        }}
      >
        <ModalInner
          ref={modalInnerRef}
          contentRef={modalInnerRef}
          isMobile={isMobile}
          onClose={() => {
            close();
            sendGTMEvent('닫기');
          }}
          onChangeExpand={(isExpand) =>
            sendGTMEvent(isExpand ? '확장해서 보기' : '축소해서 보기')
          }
          sx={{ '> .button_wrap': { zIndex: 4 } }}
          scrollToTopBottomOffset={90}
          showScrollToTopButton={showScrollToTopButton}
          maxWidth={maxDesktopWidth}
        >
          {isOpen && (
            <>
              <FormProvider {...methods}>
                <div style={{ position: 'relative' }}>
                  <TalkFLow
                    key={currentDocumentId}
                    docId={currentDocumentId}
                    layoutType={'modal'}
                    handleClose={closeModal}
                    scrollRef={modalInnerRef}
                    setShowScrollToTopButton={setShowScrollToTopButton}
                  />
                  {(uploading || isLoading) && <LayoutLoading />}
                </div>
              </FormProvider>
            </>
          )}
        </ModalInner>
      </Modal>
    );
  },
);

TalkFLowModal.displayName = 'TalkFLowModal';

export default TalkFLowModal;
